import { requirejsOne } from '@ds/swymkit/tools'
import type { CommunicationManager_AMD, TransientWidget_AMD } from '@ds/swymkit/typings'
import type { RouteLocationNormalized } from 'vue-router'

import { getSwymUrl } from '~/features-shared/config-manager/stores/swym-config'
import type { TransientWidgetConfiguration } from '~/features-shared/widget/interfaces/widget'
import { getAppName } from '~/features-shared/widget/utils/widget-app-name'

import { CONTENT_READER_APPNAME, PROFILE_APPNAME } from '~/features-generic/libs/config/widget-app-name'

/**
 * Widget names
 */
enum AppName {
  CONTENT_READER = 'content-reader',
  PROFILE = 'profile-widget',
}

/**
 * Open route in new window
 * @param to route
 */
export function openInNewTab(to: RouteLocationNormalized) {
  const url = getSwymUrl() + to.fullPath // Url = baseurl + fullpath
  window.open(url, '_blank') // To open in new window.
}

/**
 * Displays a widget preview (also known as Transient Widget).
 * @param widgetData - Transient widget config data
 * @param widgetData.appKey - The key specifying the app to instantiate or appId
 * @param widgetData.appTitle - Title of the Widget Preview. It will be concatenated to the widget "source" title (in the <title> markup).
 * @param widgetData.appData - Set of preferences of the Widget Preview or the url to retrieve the preferences.
 */
export async function showTransientWidget(widgetData: TransientWidgetConfiguration) {
  const IN_MOBILE_APP = window.ds && window.ds.env === 'MOBILE'
  if (IN_MOBILE_APP) {
    const communicationManager = await requirejsOne<CommunicationManager_AMD>
    ('DS/SwymUICore/script/feature/core/mobile/communication-manager')
    // send the app data to the webview platform
    communicationManager.sendOpenAppRequest(widgetData)
  }
  else {
    const transient = await requirejsOne<TransientWidget_AMD>('DS/TransientWidget/TransientWidget')
    transient.showWidget(widgetData.appKey, widgetData.appTitle, widgetData.appData)
  }
}

// List of all routes names that must be opened in new window corresponding to app name
const routeNameMapNewTab = {
  [AppName.CONTENT_READER]: ['community-thread_id-index', 'me-personal-space-index'],
  [AppName.PROFILE]: ['dm-thread_id-index', 'community-thread_id-index'],
}

/**
 * Check if the route is to be opened in a new window.
 * Based on:
 *  - In 3DD.
 *  - App key is content-reader or profile.(Switchcases to be used to add other app support)
 *  - Route is a valid route.
 * @param to route to check.
 * @returns true if route is to open in new window(3DD context), else false.
 */
export function toBeOpenedInNewWindow(to: RouteLocationNormalized) {
  const in3DD = window.ds?.env // 3DD-Env
  // Check if 3DD
  if (in3DD) {
    // Get current app key(Widget Key)
    const appKey = getAppName()
    switch (appKey) {
      // Handle widget routing for CRA
      case CONTENT_READER_APPNAME:
        return routeNameMapNewTab[AppName.CONTENT_READER].includes(to.name)
      // Handle widget routing for Profile widget
      case PROFILE_APPNAME:
        return routeNameMapNewTab[AppName.PROFILE].includes(to.name)
    }
  }
  return false
}

/**
 * Check if the route is to be opened in a transient widget.
 * Based on:
 *  - In 3DD.
 *  - App key is content-reader.(Switchcases to be used to add other app support)
 *  - Route is a valid route.
 * @param to route to check.
 * @returns true if route is to open in transient widget(3DD context), else false.
 */
export function toBeOpenedInTransientWidget(to: RouteLocationNormalized) {
  const in3DD = window.ds?.env // 3DD-Env
  // Check if 3DD
  if (in3DD) {
    // Get current app key(Widget Key)
    const appKey = getAppName()
    if (appKey === CONTENT_READER_APPNAME) {
      // Check if it is a valid route that needs to be opened in transient widget for content-reader context
      return to.name === 'me-profile-index' || !!to.query.profile
    }
  }
  return false
}
